import {
  defaultScenarioConfig,
  runSim,
  type AttackerDoctrine,
  type ScenarioConfig,
  type SimResult,
  type TargetDistribution,
} from "@air-defence-sim/sim-core";
import { buildCounterfactual, buildRunAnalysis, firstDetectionTime } from "./analysis.js";
import { downloadBlob, exportPng } from "./export.js";
import { makeRenderer } from "./render.js";
import { copyText, encodeConfigQuery, makeTracker, parseConfigQuery } from "./track.js";
import { mountUI } from "./ui.js";

const canvas = document.getElementById("canvas") as HTMLCanvasElement;
const overlay = document.getElementById("overlay") as HTMLElement;
const controls = document.getElementById("controls") as HTMLElement;
const scorecard = document.getElementById("scorecard") as HTMLElement;
const toolbar = document.getElementById("canvas-toolbar") as HTMLElement;
const analysisEl = document.getElementById("analysis") as HTMLElement;
const cmpEl = document.getElementById("cmp-figure") as HTMLElement;

const renderer = makeRenderer(canvas);
const tracker = makeTracker("air");

let result: SimResult | null = null;
let playing = false;
let simT = 0;
let lastFrame = 0;
let playbackSpeed = 1;
let highlight: import("./render.js").HighlightKind = null;
let exportInProgress = false;
let firstContact: number | null = null;

const ui = mountUI(controls, scorecard, overlay, toolbar, {
  onRun: () => performRun(false),
  onReplay: () => {
    if (!result) return;
    tracker.event("replay");
    startPlayback();
  },
  onHighlight: (kind) => {
    highlight = kind;
    renderer.setHighlight(kind);
    if (!playing) renderer.drawFrame(simT, 0);
  },
  onSpeedChange: (s) => {
    playbackSpeed = s;
  },
  onTrailsChange: (on) => {
    renderer.setShowTrails(on);
    if (!playing) renderer.drawFrame(simT, 0);
  },
  onThemeChange: (t) => {
    renderer.setTheme(t);
    tracker.event("theme_change", { event_label: t === "light" ? "map" : "tactical" });
    if (!playing) renderer.drawFrame(simT, 0);
  },
  onCleanViewChange: () => {
    renderer.resize();
    renderer.drawFrame(simT, 0);
  },
  onPauseToggle: () => togglePause(),
  onShareLink: async () => {
    const query = encodeConfigQuery(
      ui.getConfig() as unknown as Record<string, unknown>,
      defaultScenarioConfig({ seed: 1 }) as unknown as Record<string, unknown>,
    );
    const url = `${window.location.origin}${window.location.pathname}${query ? `?${query}` : ""}`;
    const ok = await copyText(url);
    tracker.event("share_copy");
    return ok;
  },
  onJumpToContact: () => {
    if (!result || firstContact === null) return;
    tracker.event("contact_jump");
    simT = Math.max(0, firstContact - 2);
    ui.setPlaybackTime(simT);
    if (playing) {
      lastFrame = performance.now();
    } else {
      ui.setStatus(`Paused · t=${simT.toFixed(1)}s`);
      renderer.drawFrame(simT, 0);
    }
  },
  onSeek: (t) => {
    simT = t;
    if (playing) {
      lastFrame = performance.now();
    } else {
      ui.setStatus(`Paused · t=${simT.toFixed(1)}s`);
    }
    ui.setPlaybackTime(simT);
    renderer.drawFrame(simT, 0);
  },
  onExportPng: () => {
    if (!result || exportInProgress) return;
    exportInProgress = true;
    const cfg = ui.getConfig();
    tracker.event("export_png", { event_label: `seed_${cfg.seed}` });
    tracker.shareEvent("export", `seed=${cfg.seed} t=${simT.toFixed(1)}`);
    const size = renderer.getLogicalSize();
    exportPng({
      result,
      simT,
      scale: 2,
      theme: ui.getTheme(),
      highlight,
      showTrails: ui.getTrails(),
      logicalWidth: size.width,
      logicalHeight: size.height,
    })
      .then((blob) => {
        const filename = `air-defence-sim-seed${cfg.seed}-t${simT.toFixed(1).replace(".", "_")}s.png`;
        downloadBlob(blob, filename);
      })
      .catch((err) => console.error("export failed", err))
      .finally(() => {
        exportInProgress = false;
      });
  },
});

function performRun(auto: boolean): void {
  const cfg = ui.getConfig();
  if (!auto) {
    tracker.event("run", { value: cfg.seed });
    tracker.shareEvent("run", `seed=${cfg.seed} budget=${cfg.totalDefenceBudget} mobile=${cfg.mobileDefenceBudgetShare}`);
  }
  ui.setStatus("Running…");
  result = runSim(cfg);
  renderer.setResult(result);
  ui.setMetrics(result.metrics);
  ui.setReplayEnabled(true);
  ui.setExportEnabled(true);
  ui.setPlaybackBounds(playbackEndTime(result));
  firstContact = firstDetectionTime(result);
  ui.setContactEnabled(firstContact !== null);
  // Same-seed counterfactual with the allocation flipped: the convoy sim taught us
  // that "same attack, same dice, different posture" is the comparison people trust.
  const cfShare = cfg.mobileDefenceBudgetShare >= 0.35 ? 0 : 0.7;
  const cf = runSim({ ...cfg, mobileDefenceBudgetShare: cfShare });
  analysisEl.innerHTML = buildRunAnalysis(cfg, result);
  cmpEl.innerHTML = buildCounterfactual(result, cf, cfShare);
  startPlayback();
}

playbackSpeed = ui.getSpeed();
renderer.setShowTrails(ui.getTrails());
renderer.setTheme(ui.getTheme());

ui.setStatus("Press Run simulation");
renderer.resize();
renderer.drawFrame(0, 0);

// Config from a share URL: apply, mark custom, and auto-run so the recipient
// lands on the exact scenario the sender saw.
const URL_KEYS: Array<keyof ScenarioConfig> = [
  "totalDefenceBudget",
  "mobileDefenceBudgetShare",
  "targetDistribution",
  "targetValueEntropy",
  "attackerConcentration",
  "attackerDoctrine",
  "warningRangeMultiplier",
  "attackerWeaponsPerWave",
  "attackerWaveCount",
  "seed",
];
const DISTRIBUTIONS: TargetDistribution[] = ["country", "concentrated", "clustered", "distributed"];
const DOCTRINES: AttackerDoctrine[] = ["concentrate", "disperse", "probe", "mixed"];
{
  const raw = parseConfigQuery(defaultScenarioConfig({ seed: 1 }) as unknown as Record<string, unknown>);
  const cfg: Partial<ScenarioConfig> = {};
  for (const key of URL_KEYS) {
    const v = raw[key];
    if (v === undefined) continue;
    if (key === "targetDistribution") {
      if (DISTRIBUTIONS.includes(v as TargetDistribution)) cfg.targetDistribution = v as TargetDistribution;
    } else if (key === "attackerDoctrine") {
      if (DOCTRINES.includes(v as AttackerDoctrine)) cfg.attackerDoctrine = v as AttackerDoctrine;
    } else if (typeof v === "number" && Number.isFinite(v)) {
      (cfg as Record<string, unknown>)[key] = v;
    }
  }
  if (Object.keys(cfg).length > 0) {
    ui.applyConfig(cfg);
    performRun(true);
  }
}

window.addEventListener("resize", () => {
  renderer.resize();
  renderer.drawFrame(simT, 0);
});

document.addEventListener("keydown", (e) => {
  if (e.code !== "Space") return;
  const tag = document.activeElement?.tagName;
  if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA" || tag === "BUTTON") return;
  e.preventDefault();
  togglePause();
});

function togglePause(): void {
  if (!result) return;
  if (playing) {
    playing = false;
    ui.setPlaying(false);
    ui.setStatus(`Paused · t=${simT.toFixed(1)}s`);
  } else {
    const endT = playbackEndTime(result);
    if (simT >= endT) simT = 0;
    playing = true;
    ui.setPlaying(true);
    lastFrame = performance.now();
    requestAnimationFrame(tick);
  }
}

function startPlayback(): void {
  if (!result) return;
  simT = 0;
  playing = true;
  lastFrame = performance.now();
  ui.setPlaying(true);
  ui.setPlaybackTime(0);
  requestAnimationFrame(tick);
}

function formatSpeed(s: number): string {
  return s < 1 ? s.toString() : Math.round(s).toString();
}

function playbackEndTime(r: SimResult): number {
  let last = 0;
  for (const e of r.events) if (e.t > last) last = e.t;
  return last + 2;
}

function tick(now: number): void {
  if (!result) return;
  const realDt = (now - lastFrame) / 1000;
  lastFrame = now;
  if (playing) {
    simT += realDt * playbackSpeed;
    const endT = playbackEndTime(result);
    if (simT >= endT) {
      simT = endT;
      playing = false;
      ui.setPlaying(false);
      ui.setStatus(`Done · t=${simT.toFixed(1)}s`);
    } else {
      ui.setStatus(`t=${simT.toFixed(1)}s · ${formatSpeed(playbackSpeed)}× speed`);
    }
    ui.setPlaybackTime(simT);
  }
  renderer.drawFrame(simT, 0);
  if (playing) requestAnimationFrame(tick);
}
